import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import type { Task } from "@recobra/schemas/task";
import { systemTaskType } from "@recobra/schemas/task-catalog";
import { DatePicker } from "@recobra/ui/custom/date-picker";
import { Field, FieldError } from "@recobra/ui/field";

import { UserLabel } from "../../lib";
import { TaskAssigneePicker } from "../task-display";
import { TaskContextCaseSummary } from "../task-display";
import { formatTaskDate } from "../task-display";
import {
  TaskModalSection,
  TaskSummaryColumns,
  TaskSummaryList,
  TaskSummaryRow,
} from "../task-display";
import { TaskAssigneeEmailSection } from "../task-emails";
import { TaskRemindersSection } from "../task-reminders";
import { TaskCaseExceptionDocuments } from "./TaskCaseExceptionDocuments";
import { TaskMetadataFieldActions } from "./TaskMetadataFieldActions";
import { TaskDvcMetadata, TaskRequestedDocumentsMetadata } from "./TaskMetadataSections";
import { TaskPreviousActivitySummary } from "./TaskPreviousActivitySummary";
import type { TaskMetadataEditor } from "./useTaskMetadataEditor";

export const TaskDetailsSummary: FC<{ task: Task; editor: TaskMetadataEditor }> = ({
  task,
  editor,
}) => {
  const description = task.description?.trim();
  const collects = systemTaskType(task.system)?.collects;
  const deadlineLabel = task.deadlineDate ? formatTaskDate(task.deadlineDate) : m.common_em_dash();

  return (
    <>
      <TaskModalSection>
        <TaskSummaryColumns>
          <TaskContextCaseSummary task={task} />
          <TaskSummaryList>
            <TaskSummaryRow
              label={m.task_field_deadline()}
              action={<TaskMetadataFieldActions field="deadlineDate" editor={editor} />}
            >
              {editor.editing === "deadlineDate" ? (
                <editor.form.AppField name="deadlineDate">
                  {(field) => (
                    <Field data-invalid={!field.state.meta.isValid}>
                      <DatePicker
                        value={field.state.value}
                        onChange={(value) => field.handleChange(value ?? "")}
                        onBlur={field.handleBlur}
                        disabled={editor.isPending}
                        required
                        aria-label={m.task_field_deadline()}
                        aria-invalid={!field.state.meta.isValid}
                      />
                      <FieldError>{field.state.meta.errors[0]}</FieldError>
                    </Field>
                  )}
                </editor.form.AppField>
              ) : (
                deadlineLabel
              )}
            </TaskSummaryRow>
            <TaskAssigneeRow task={task} editor={editor} />
          </TaskSummaryList>
        </TaskSummaryColumns>
      </TaskModalSection>

      <TaskAssigneeEmailSection task={task} />

      {collects?.dvcSearchDetails && <TaskDvcMetadata task={task} editor={editor} />}

      {collects?.requestedDocuments && task.requestedDocuments.length === 0 && (
        <TaskRequestedDocumentsMetadata task={task} editor={editor} />
      )}

      <TaskPreviousActivitySummary task={task} />

      <TaskRemindersSection task={task} />

      {task.system === "review_case_exception" ? (
        <TaskModalSection>
          <TaskCaseExceptionDocuments task={task} />
        </TaskModalSection>
      ) : null}

      {description ? (
        <TaskModalSection label={m.task_field_description()}>
          <p className="text-sm whitespace-pre-wrap">{description}</p>
        </TaskModalSection>
      ) : null}
    </>
  );
};

const TaskAssigneeRow: FC<{ task: Task; editor: TaskMetadataEditor }> = ({ task, editor }) => {
  if (!editor.canViewMetadata) return null;

  return (
    <TaskSummaryRow
      label={m.task_field_assignee()}
      action={<TaskMetadataFieldActions field="assignee" editor={editor} />}
    >
      {editor.editing === "assignee" ? (
        <editor.form.AppField name="assignedTo">
          {(field) => (
            <TaskAssigneePicker
              taskType={task.type}
              contextId={task.contextId}
              contextType={task.contextType}
              value={field.state.value}
              enabled
              isSaving={editor.isPending}
              onChange={field.handleChange}
            />
          )}
        </editor.form.AppField>
      ) : (
        <UserLabel userId={task.assignedTo} emptyLabel={m.task_field_assignee_unassigned()} />
      )}
    </TaskSummaryRow>
  );
};
